// Strudel IntelliSense — context-aware completions for the source editor.
//
// Strategy: a single composed CompletionSource that first works out
// what kind of context the cursor is in, then returns the slice of the
// knowledge base relevant to that context. Pure heuristics — no AST.
//
// Contexts handled:
//   stringInside: 's("…|…")'        → SAMPLE_NAMES
//   stringInside: 'note("…|…")'     → NOTE_LITERALS
//   stringInside: '.s("…|…")'       → SYNTH_WAVEFORMS
//   stringInside: '.scale("…|…")'   → SCALE_NAMES
//   stringInside: '.chord("…|…")'   → CHORD_SYMBOLS
//   chained:      '<expr>.|'        → ENTRIES with scope chain
//   topLevel:     '|' or after '\n' → ENTRIES with scope top
//
// The popup itself is themed via EditorView.theme to match the harness
// (zinc-950 surface, yellow-300 phosphor edge on the active row, tiny
// uppercase tracking labels). The .info panel renders a richer detail
// card with signature + description + working code example.

import {
  autocompletion,
  snippetCompletion,
  type Completion,
  type CompletionContext,
  type CompletionResult,
  type CompletionSource,
} from '@codemirror/autocomplete';
import { EditorView } from '@codemirror/view';
import {
  CHORD_SYMBOLS,
  ENTRIES,
  NOTE_LITERALS,
  SAMPLE_NAMES,
  SCALE_NAMES,
  SYNTH_WAVEFORMS,
  type EntryCategory,
  type StrudelEntry,
} from './data';

// ─── Category visuals ────────────────────────────────────────────────

// Hex tokens kept here so the info panel and the dot match without a
// trip through Tailwind class lookup. Mirrors the broader app palette.
const CATEGORY_COLOR: Record<EntryCategory, string> = {
  tempo: '#fde047',     // yellow-300
  container: '#fde047', // yellow-300
  source: '#6ee7b7',    // emerald-300
  filter: '#67e8f9',    // cyan-300
  amp: '#67e8f9',       // cyan-300
  space: '#c4b5fd',     // violet-300
  shape: '#c4b5fd',     // violet-300
  pitch: '#7dd3fc',     // sky-300
  transform: '#7dd3fc', // sky-300
  cond: '#fda4af',      // rose-300
  sampler: '#fcd34d',   // amber-300
  io: '#a1a1aa',        // zinc-400
};

const CATEGORY_LABEL: Record<EntryCategory, string> = {
  tempo: 'tempo',
  container: 'container',
  source: 'source',
  filter: 'filter',
  amp: 'amp',
  space: 'space',
  shape: 'shape',
  pitch: 'pitch',
  transform: 'transform',
  cond: 'cond',
  sampler: 'sampler',
  io: 'i/o',
};

// ─── Entry → CodeMirror Completion conversion ────────────────────────

function entryToCompletion(entry: StrudelEntry): Completion {
  const completion = snippetCompletion(entry.snippet, {
    label: entry.name,
    detail: entry.detail,
    type: entry.category,
    boost: entry.boost ?? 0,
    info: () => buildInfoNode(entry),
  });
  return completion;
}

function literalToCompletion(
  item: { name: string; hint: string },
  type: string,
): Completion {
  return {
    label: item.name,
    detail: item.hint,
    type,
    apply: item.name,
  };
}

// ─── Info (peek) panel ──────────────────────────────────────────────

function buildInfoNode(entry: StrudelEntry): HTMLElement {
  const root = document.createElement('div');
  root.className = 'cm-strudel-info';

  // Header row — colored category label + signature.
  const header = document.createElement('div');
  header.className = 'cm-strudel-info-header';
  const tag = document.createElement('span');
  tag.className = 'cm-strudel-info-tag';
  tag.textContent = CATEGORY_LABEL[entry.category];
  tag.style.color = CATEGORY_COLOR[entry.category];
  header.appendChild(tag);
  const sig = document.createElement('code');
  sig.className = 'cm-strudel-info-sig';
  sig.textContent = entry.signature;
  header.appendChild(sig);
  root.appendChild(header);

  // Description paragraph.
  const doc = document.createElement('p');
  doc.className = 'cm-strudel-info-doc';
  doc.textContent = entry.doc;
  root.appendChild(doc);

  // Example block.
  const exampleLabel = document.createElement('div');
  exampleLabel.className = 'cm-strudel-info-label';
  exampleLabel.textContent = 'example';
  root.appendChild(exampleLabel);

  const example = document.createElement('pre');
  example.className = 'cm-strudel-info-example';
  example.textContent = entry.example;
  root.appendChild(example);

  // Footer hint — keyboard shortcuts.
  const hint = document.createElement('div');
  hint.className = 'cm-strudel-info-hint';
  hint.innerHTML =
    '<span><kbd>↵</kbd> insert</span>' +
    '<span><kbd>tab</kbd> next field</span>' +
    '<span><kbd>esc</kbd> close</span>';
  root.appendChild(hint);

  return root;
}

// ─── Context detection ──────────────────────────────────────────────

type StringContext = {
  kind: 'sample' | 'note' | 'synth' | 'scale' | 'chord';
  from: number;
  to: number;
};

// Walk back from the cursor to find an unmatched opening quote on the
// same line, then look at the function name immediately preceding the
// `(` that contains it. This is heuristic but reliable for the
// well-formed Strudel patterns we care about.
function detectStringContext(
  context: CompletionContext,
): StringContext | null {
  const { state, pos } = context;
  const line = state.doc.lineAt(pos);
  const lineStart = line.from;
  const lineText = state.doc.sliceString(lineStart, pos);

  // Find the most recent unescaped quote on this line, scanning backward.
  // We only count it as "opening" if the number of same-style quotes from
  // here to end-of-fragment is odd — meaning the cursor sits inside it.
  let openQuote = -1;
  for (let i = lineText.length - 1; i >= 0; i--) {
    const ch = lineText[i];
    if ((ch !== '"' && ch !== "'") || lineText[i - 1] === '\\') continue;
    let count = 0;
    for (let j = i; j < lineText.length; j++) {
      if (lineText[j] === ch && lineText[j - 1] !== '\\') count++;
    }
    if (count % 2 === 1) {
      openQuote = i;
      break;
    }
  }
  if (openQuote === -1) return null;

  // The text between openQuote+1 and pos is the in-string fragment we
  // want to complete. The token immediately before openQuote tells us
  // which list to use. The optional `.?` in the regex captures whether
  // the call is chained (`.s(`) or a top-level call (`s(`).
  const before = lineText.slice(0, openQuote);
  const fnMatch = before.match(
    /(?:^|[^a-zA-Z0-9_$])(\.?)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*\(\s*$/,
  );
  if (!fnMatch) return null;
  const wasChained = fnMatch[1] === '.';
  const fn = fnMatch[2];

  // Token-of-current-fragment — walk back from the cursor over identifier
  // characters so the popup replaces just the token under the caret, not
  // the whole literal (lets the user keep typing whitespace-separated
  // names like `bd hh`).
  const fragmentStartInLine = openQuote + 1;
  const fragText = lineText.slice(fragmentStartInLine);
  let tokStart = fragText.length;
  while (
    tokStart > 0 &&
    /[A-Za-z0-9_:#\-]/.test(fragText[tokStart - 1] ?? '')
  ) {
    tokStart--;
  }
  const from = lineStart + fragmentStartInLine + tokStart;
  const to = pos;

  let kind: StringContext['kind'] | null = null;
  if (fn === 's') {
    kind = wasChained ? 'synth' : 'sample';
  } else if (fn === 'note') {
    kind = 'note';
  } else if (fn === 'scale') {
    kind = 'scale';
  } else if (fn === 'chord') {
    kind = 'chord';
  } else if (fn === 'sound') {
    // Strudel alias for s
    kind = wasChained ? 'synth' : 'sample';
  }
  if (!kind) return null;
  return { kind, from, to };
}

// Is the cursor in a "chain" position — directly after `.`, possibly
// across whitespace, with an identifier/expression before? Returns the
// range we should replace if the user types more after the dot.
function detectChainContext(
  context: CompletionContext,
): { from: number; to: number } | null {
  // matchBefore looks at chars immediately before cursor.
  // Pattern: optional identifier (the partial method name), prefixed by `.`
  const m = context.matchBefore(/\.([a-zA-Z_$][a-zA-Z0-9_$]*)?/);
  if (!m) return null;
  // m.text starts with `.` — we want the completion to replace just the
  // identifier portion (after the dot).
  const dotPos = m.from;
  return { from: dotPos + 1, to: m.to };
}

// Top-level / generic identifier position. Matches a partial word.
function detectWordContext(
  context: CompletionContext,
): { from: number; to: number; explicit: boolean } | null {
  const m = context.matchBefore(/[a-zA-Z_$][a-zA-Z0-9_$]*/);
  if (!m) {
    // Allow explicit (Ctrl-Space) trigger with no token.
    if (context.explicit) return { from: context.pos, to: context.pos, explicit: true };
    return null;
  }
  if (m.from === m.to && !context.explicit) return null;
  return { from: m.from, to: m.to, explicit: context.explicit };
}

// ─── Composed source ────────────────────────────────────────────────

const CHAINED_COMPLETIONS = ENTRIES.filter((e) => e.scope === 'chain' || e.scope === 'both').map(entryToCompletion);
const TOP_COMPLETIONS = ENTRIES.filter((e) => e.scope === 'top' || e.scope === 'both').map(entryToCompletion);

const SAMPLE_COMPLETIONS = SAMPLE_NAMES.map((s) => literalToCompletion(s, 'sampler'));
const NOTE_COMPLETIONS = NOTE_LITERALS.map((n) => literalToCompletion(n, 'pitch'));
const SYNTH_COMPLETIONS = SYNTH_WAVEFORMS.map((s) => literalToCompletion(s, 'source'));
const SCALE_COMPLETIONS = SCALE_NAMES.map((s) => literalToCompletion(s, 'pitch'));
const CHORD_COMPLETIONS = CHORD_SYMBOLS.map((c) => literalToCompletion(c, 'pitch'));

const strudelSource: CompletionSource = (context) => {
  // 1. In-string contexts get highest priority.
  const stringCtx = detectStringContext(context);
  if (stringCtx) {
    const opts = stringOptions(stringCtx.kind);
    const result: CompletionResult = {
      from: stringCtx.from,
      to: stringCtx.to,
      options: opts,
      validFor: /^[A-Za-z0-9_:#\-]*$/,
    };
    return result;
  }

  // 2. Chain context — show only chainable methods.
  const chainCtx = detectChainContext(context);
  if (chainCtx) {
    return {
      from: chainCtx.from,
      to: chainCtx.to,
      options: CHAINED_COMPLETIONS,
      validFor: /^[a-zA-Z_$][a-zA-Z0-9_$]*$/,
    };
  }

  // 3. Word / top-level position.
  const wordCtx = detectWordContext(context);
  if (!wordCtx) return null;
  // If the user typed nothing and the trigger wasn't explicit, bail —
  // we never want the popup leaping out unprompted on a blank line.
  if (wordCtx.from === wordCtx.to && !wordCtx.explicit) return null;
  return {
    from: wordCtx.from,
    to: wordCtx.to,
    options: TOP_COMPLETIONS,
    validFor: /^[a-zA-Z_$][a-zA-Z0-9_$]*$/,
  };
};

function stringOptions(kind: StringContext['kind']): readonly Completion[] {
  switch (kind) {
    case 'sample':
      return SAMPLE_COMPLETIONS;
    case 'note':
      return NOTE_COMPLETIONS;
    case 'synth':
      return SYNTH_COMPLETIONS;
    case 'scale':
      return SCALE_COMPLETIONS;
    case 'chord':
      return CHORD_COMPLETIONS;
  }
}

// ─── Theme ──────────────────────────────────────────────────────────

const completionTheme = EditorView.theme({
  '.cm-tooltip.cm-tooltip-autocomplete': {
    border: 'none',
    background: 'transparent',
    boxShadow: 'none',
    // Position offset so the popup floats just below the line w/ breath.
    marginTop: '4px',
  },
  '.cm-tooltip.cm-tooltip-autocomplete > ul': {
    fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
    fontSize: '12px',
    lineHeight: '1.45',
    maxHeight: '300px',
    minWidth: '288px',
    maxWidth: '288px',
    background: 'rgba(9, 9, 11, 0.94)',
    border: '1px solid rgb(39 39 42)',
    borderRadius: '4px',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    boxShadow:
      '0 0 0 1px rgba(0,0,0,0.4), 0 12px 32px -8px rgba(0,0,0,0.7), 0 0 24px -8px rgba(253, 224, 71, 0.15)',
    padding: '4px 0',
    color: '#e4e4e7',
    overflow: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgb(63 63 70) transparent',
  },
  '.cm-tooltip.cm-tooltip-autocomplete > ul > li': {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    padding: '4px 12px 4px 14px',
    cursor: 'pointer',
    position: 'relative',
    borderLeft: '3px solid transparent',
  },
  '.cm-tooltip.cm-tooltip-autocomplete > ul > li[aria-selected]': {
    background: 'rgba(253, 224, 71, 0.06)',
    borderLeftColor: 'rgb(253, 224, 71)',
    boxShadow:
      'inset 0 0 0 0 transparent, 0 0 18px -6px rgba(253, 224, 71, 0.25)',
  },
  '.cm-tooltip.cm-tooltip-autocomplete > ul > li[aria-selected] .cm-completionLabel': {
    color: '#fef9c3',
  },
  '.cm-completionLabel': {
    color: '#fafafa',
    fontWeight: '500',
  },
  '.cm-completionMatchedText': {
    color: '#fde047',
    textDecoration: 'none',
    fontWeight: '600',
  },
  '.cm-completionDetail': {
    color: '#71717a',
    fontStyle: 'normal',
    fontSize: '10px',
    letterSpacing: '0.04em',
    marginLeft: 'auto',
    paddingLeft: '8px',
    flexShrink: '0',
    textTransform: 'lowercase',
  },
  // Type icons — repurposed as the colored category dot.
  '.cm-completionIcon': {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    boxShadow: '0 0 4px currentColor',
    opacity: '1',
    fontSize: '0',
    margin: '0',
    paddingRight: '0',
    flexShrink: '0',
    alignSelf: 'center',
  },
  '.cm-completionIcon::after': { content: '""' },
  // Map each known type to its colour. Keep in sync with CATEGORY_COLOR.
  '.cm-completionIcon-tempo, .cm-completionIcon-container': { color: '#fde047' },
  '.cm-completionIcon-source': { color: '#6ee7b7' },
  '.cm-completionIcon-filter, .cm-completionIcon-amp': { color: '#67e8f9' },
  '.cm-completionIcon-space, .cm-completionIcon-shape': { color: '#c4b5fd' },
  '.cm-completionIcon-pitch, .cm-completionIcon-transform': { color: '#7dd3fc' },
  '.cm-completionIcon-cond': { color: '#fda4af' },
  '.cm-completionIcon-sampler': { color: '#fcd34d' },
  '.cm-completionIcon-io': { color: '#a1a1aa' },

  // ─── Info (peek) panel ───
  '.cm-tooltip.cm-completionInfo': {
    fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
    background: 'rgba(9, 9, 11, 0.96)',
    border: '1px solid rgb(39 39 42)',
    borderRadius: '4px',
    color: '#e4e4e7',
    padding: '0',
    marginLeft: '8px',
    minWidth: '320px',
    maxWidth: '380px',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    boxShadow:
      '0 0 0 1px rgba(0,0,0,0.4), 0 14px 36px -10px rgba(0,0,0,0.75), 0 0 28px -8px rgba(253, 224, 71, 0.18)',
    animation: 'cm-strudel-info-in 160ms cubic-bezier(0.2, 0.7, 0.2, 1)',
  },
  '.cm-strudel-info': {
    padding: '0',
  },
  '.cm-strudel-info-header': {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
    padding: '10px 12px 8px',
    borderBottom: '1px solid rgba(39, 39, 42, 0.6)',
    background: 'linear-gradient(180deg, rgba(253, 224, 71, 0.04) 0%, transparent 100%)',
  },
  '.cm-strudel-info-tag': {
    fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
    fontSize: '8.5px',
    letterSpacing: '0.32em',
    textTransform: 'uppercase',
    fontWeight: '600',
    flexShrink: '0',
  },
  '.cm-strudel-info-sig': {
    fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
    fontSize: '12px',
    color: '#fafafa',
    fontWeight: '500',
    background: 'transparent',
    padding: '0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '.cm-strudel-info-doc': {
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: '11.5px',
    lineHeight: '1.5',
    color: '#a1a1aa',
    padding: '10px 12px 4px',
    margin: '0',
  },
  '.cm-strudel-info-label': {
    fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
    fontSize: '8.5px',
    letterSpacing: '0.32em',
    textTransform: 'uppercase',
    color: '#52525b',
    padding: '8px 12px 4px',
  },
  '.cm-strudel-info-example': {
    fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
    fontSize: '11px',
    lineHeight: '1.55',
    background: 'rgba(0, 0, 0, 0.4)',
    color: '#d4d4d8',
    margin: '0 12px 10px',
    padding: '8px 10px',
    borderLeft: '2px solid rgba(253, 224, 71, 0.55)',
    borderRadius: '0 3px 3px 0',
    overflow: 'auto',
    whiteSpace: 'pre',
    maxHeight: '160px',
  },
  '.cm-strudel-info-hint': {
    display: 'flex',
    gap: '14px',
    padding: '8px 12px',
    borderTop: '1px solid rgba(39, 39, 42, 0.6)',
    fontSize: '9.5px',
    color: '#52525b',
    letterSpacing: '0.06em',
    background: 'rgba(0, 0, 0, 0.25)',
    borderRadius: '0 0 3px 3px',
  },
  '.cm-strudel-info-hint kbd': {
    fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
    fontSize: '9.5px',
    background: 'rgba(63, 63, 70, 0.6)',
    color: '#d4d4d8',
    padding: '1px 5px',
    borderRadius: '2px',
    border: '1px solid rgba(82, 82, 91, 0.5)',
    marginRight: '4px',
  },

  // Snippet field highlight — yellow phosphor underline on active stop.
  '.cm-snippetField': {
    background: 'rgba(253, 224, 71, 0.08)',
    outline: '1px solid rgba(253, 224, 71, 0.5)',
    borderRadius: '2px',
  },
  '.cm-snippetFieldPosition': {
    borderLeft: '2px solid rgba(253, 224, 71, 0.7)',
  },
});

// ─── Public extension ───────────────────────────────────────────────

export const strudelCompletions = [
  autocompletion({
    override: [strudelSource],
    activateOnTyping: true,
    closeOnBlur: true,
    icons: true,
    selectOnOpen: true,
    maxRenderedOptions: 60,
    defaultKeymap: true,
    // The info panel needs no extra delay — discoverability matters
    // more than restraint here.
    tooltipClass: () => 'cm-strudel-tip',
  }),
  completionTheme,
];
